import React from 'react';
import { useAuth } from '../../contexts/AuthContext';
import {
  Button,
  Card,
  DataTable,
  DataTableColumn,
  TextInput,
  ToastProvider,
  useToast
} from '../../design-system';
import { voiceApi } from './voiceClient';

interface Props { storeId: string; }

interface CompactItem {
  n: string;        // name
  p: number;        // price
  c?: string;       // category
  m?: Array<{ n: string; p: number }>;  // modifiers
  x?: boolean;      // 86'd / unavailable
}

interface ContextPayload {
  storeId: string;
  bytes: number;
  itemCount: number;
  compiledAt: string;
  context: {
    storeId: string;
    storeName?: string;
    items: CompactItem[];
  };
}

const Inner: React.FC<Props> = ({ storeId }) => {
  const { getIdToken } = useAuth();
  const { show } = useToast();
  const [data, setData] = React.useState<ContextPayload | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [rebuilding, setRebuilding] = React.useState(false);
  const [filter, setFilter] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const r: any = await voiceApi.getContext(getIdToken, storeId);
      setData(r);
    } catch (err: any) {
      setData(null);
      setError(err.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  }, [getIdToken, storeId]);

  React.useEffect(() => { load(); }, [load]);

  const handleRebuild = async () => {
    setRebuilding(true);
    try {
      await voiceApi.rebuildContext(getIdToken, storeId);
      show({ tone: 'success', title: 'Context rebuilt' });
      await load();
    } catch (err: any) {
      show({ tone: 'error', title: 'Rebuild failed', description: err.message });
    } finally {
      setRebuilding(false);
    }
  };

  const items = data?.context.items ?? [];
  const filtered = React.useMemo(() => {
    if (!filter) return items;
    const f = filter.toLowerCase();
    return items.filter((it) => it.n.toLowerCase().includes(f) || (it.c && it.c.toLowerCase().includes(f)));
  }, [items, filter]);

  const unavailable = items.filter((it) => it.x).length;
  const withMods = items.filter((it) => it.m && it.m.length > 0).length;
  const bytes = data?.bytes ?? 0;
  const pctOfCap = Math.round((bytes / (50 * 1024)) * 100);

  const columns: DataTableColumn<CompactItem>[] = [
    { key: 'n', header: 'Name', accessor: (r) => r.n, sortable: true, compare: (a, b) => a.n.localeCompare(b.n) },
    { key: 'c', header: 'Category', accessor: (r) => r.c || '—', sortable: true, compare: (a, b) => (a.c || '').localeCompare(b.c || '') },
    { key: 'p', header: 'Price', accessor: (r) => `$${r.p.toFixed(2)}`, align: 'right', sortable: true, compare: (a, b) => a.p - b.p },
    {
      key: 'mods',
      header: 'Modifiers',
      accessor: (r) => r.m && r.m.length ? `${r.m.length}` : '—',
      align: 'right'
    },
    {
      key: 'state',
      header: 'Availability',
      accessor: (r) => r.x
        ? <span style={{ color: 'var(--bso-danger)', fontSize: 12, fontWeight: 500 }}>86'd</span>
        : <span style={{ color: 'var(--bso-success)', fontSize: 12, fontWeight: 500 }}>Available</span>,
      align: 'center'
    }
  ];

  return (
    <div data-theme="dark" style={{ padding: 'var(--bso-space-8) var(--bso-space-10)', background: 'var(--bso-bg-base)', color: 'var(--bso-text-primary)', fontFamily: 'var(--bso-font-sans)' }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 'var(--bso-space-6)' }}>
        <div>
          <h1 style={{ margin: '0 0 var(--bso-space-2)' }}>Menu context inspector</h1>
          <p style={{ color: 'var(--bso-text-secondary)', margin: 0, maxWidth: 640 }}>
            The compiled menu JSON sent to xAI Grok (session.update) and to the
            Jetson edge voice engine. This is the source of truth the voice
            model sees — mismatches between this view and the live menu are
            the usual cause of wrong-item errors.
          </p>
        </div>
        <div style={{ display: 'flex', gap: 'var(--bso-space-3)' }}>
          <Button variant="ghost" onClick={load}>Refresh</Button>
          <Button onClick={handleRebuild} loading={rebuilding}>Rebuild from menu</Button>
        </div>
      </header>

      {error && !data && (
        <Card>
          <Card.Body>
            <p style={{ margin: 0, color: 'var(--bso-text-secondary)' }}>
              No compiled context for this store yet. Click <strong>Rebuild from menu</strong> above.
            </p>
          </Card.Body>
        </Card>
      )}

      {data && (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 'var(--bso-space-4)', marginBottom: 'var(--bso-space-6)' }}>
            <Stat label="Items" value={items.length.toLocaleString()} />
            <Stat label="Size" value={`${(bytes / 1024).toFixed(1)} KB`} tone={pctOfCap > 85 ? 'warning' : undefined} />
            <Stat label="% of 50 KB cap" value={`${pctOfCap}%`} tone={pctOfCap > 85 ? 'warning' : undefined} />
            <Stat label="With modifiers" value={withMods.toLocaleString()} />
            <Stat label="86'd" value={unavailable.toLocaleString()} tone={unavailable > 0 ? 'warning' : undefined} />
            <Stat label="Last compiled" value={new Date(data.compiledAt).toLocaleString()} />
          </div>

          <Card>
            <Card.Header
              title={`${filtered.length} of ${items.length} items`}
              actions={
                <div style={{ minWidth: 280 }}>
                  <TextInput
                    aria-label="Filter items"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    placeholder="Filter by name or category"
                  />
                </div>
              }
            />
            <Card.Body>
              <DataTable<CompactItem>
                columns={columns}
                rows={filtered}
                getRowKey={(_, i) => `${_.n}-${i}`}
                pageSize={25}
                zebra
                emptyState="No items match."
              />
            </Card.Body>
          </Card>
        </>
      )}
    </div>
  );
};

const Stat: React.FC<{ label: string; value: string; tone?: 'warning' | 'danger' }> = ({ label, value, tone }) => {
  const color = tone === 'danger' ? 'var(--bso-danger)' : tone === 'warning' ? 'var(--bso-warning)' : 'var(--bso-text-primary)';
  return (
    <Card>
      <Card.Body>
        <div style={{ fontSize: 'var(--bso-text-xs)', color: 'var(--bso-text-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{label}</div>
        <div style={{ fontSize: 'var(--bso-text-2xl)', fontWeight: 'var(--bso-weight-semibold)', color, marginTop: 'var(--bso-space-2)' }}>{value}</div>
      </Card.Body>
    </Card>
  );
};

export const VoiceContextInspector: React.FC<Props> = (props) => (
  <ToastProvider>
    <Inner {...props} />
  </ToastProvider>
);
